/**
 * Product Image Zoom Component
 * Provides click-to-zoom lightbox functionality for product images
 */

class ProductImageZoom {
  constructor() {
    this.modal = null;
    this.currentIndex = 0;
    this.images = [];
    this.zoomLevel = 1;
    this.maxZoom = 3;
    this.minZoom = 1;
    this.zoomStep = 0.5;
    this.isDragging = false;
    this.dragStart = { x: 0, y: 0 };
    this.imagePosition = { x: 0, y: 0 };

    this.init();
  }

  init() {
    this.createModal();
    this.bindProductImageClicks();
    this.bindModalEvents();
  }

  createModal() {
    const modal = document.createElement('div');
    modal.className = 'product-zoom-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Product image zoom view');

    modal.innerHTML = `
      <div class="product-zoom-modal__overlay"></div>
      <div class="product-zoom-modal__container">
        <button class="product-zoom-modal__close" aria-label="Close zoom view">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div class="product-zoom-modal__counter" style="display: none;">
          <span class="current">1</span> / <span class="total">1</span>
        </div>

        <button class="product-zoom-modal__nav product-zoom-modal__nav--prev" aria-label="Previous image">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div class="product-zoom-modal__content">
          <div class="product-zoom-modal__loading">
            <div class="product-zoom-modal__loading-spinner"></div>
          </div>
          <div class="product-zoom-modal__image-wrapper">
            <img class="product-zoom-modal__image" src="" alt="" />
          </div>
        </div>

        <button class="product-zoom-modal__nav product-zoom-modal__nav--next" aria-label="Next image">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <div class="product-zoom-modal__controls">
          <button class="product-zoom-modal__zoom-btn product-zoom-modal__zoom-in" aria-label="Zoom in">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
            </svg>
          </button>
          <button class="product-zoom-modal__zoom-btn product-zoom-modal__zoom-out" aria-label="Zoom out">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
            </svg>
          </button>
          <button class="product-zoom-modal__zoom-btn product-zoom-modal__zoom-reset" aria-label="Reset zoom">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        <div class="product-zoom-modal__thumbnails"></div>
      </div>
    `;

    document.body.appendChild(modal);
    this.modal = modal;
  }

  bindProductImageClicks() {
    // Delegate click events for product images
    document.addEventListener('click', (e) => {
      const mediaItem = e.target.closest('.product__media-item');
      if (mediaItem) {
        const img = mediaItem.querySelector('img');
        if (img && e.target.tagName === 'IMG') {
          e.preventDefault();
          this.openZoom(mediaItem);
        }
      }
    });

    // Add zoom indicators to product images
    this.addZoomIndicators();
  }

  addZoomIndicators() {
    const mediaItems = document.querySelectorAll('.product__media-item');
    mediaItems.forEach(item => {
      if (item.querySelector('img') && !item.querySelector('.product-zoom-indicator')) {
        const indicator = document.createElement('div');
        indicator.className = 'product-zoom-indicator';
        indicator.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
          </svg>
          <span>Click to zoom</span>
        `;
        item.appendChild(indicator);
      }
    });
  }

  bindModalEvents() {
    // Close button
    const closeBtn = this.modal.querySelector('.product-zoom-modal__close');
    closeBtn.addEventListener('click', () => this.close());

    // Overlay click
    const overlay = this.modal.querySelector('.product-zoom-modal__overlay');
    overlay.addEventListener('click', () => this.close());

    // Navigation
    const prevBtn = this.modal.querySelector('.product-zoom-modal__nav--prev');
    const nextBtn = this.modal.querySelector('.product-zoom-modal__nav--next');

    prevBtn.addEventListener('click', () => this.navigate(-1));
    nextBtn.addEventListener('click', () => this.navigate(1));

    // Zoom controls
    const zoomIn = this.modal.querySelector('.product-zoom-modal__zoom-in');
    const zoomOut = this.modal.querySelector('.product-zoom-modal__zoom-out');
    const zoomReset = this.modal.querySelector('.product-zoom-modal__zoom-reset');

    zoomIn.addEventListener('click', () => this.zoom(this.zoomStep));
    zoomOut.addEventListener('click', () => this.zoom(-this.zoomStep));
    zoomReset.addEventListener('click', () => this.resetZoom());

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!this.modal.classList.contains('active')) return;

      switch (e.key) {
        case 'Escape':
          this.close();
          break;
        case 'ArrowLeft':
          this.navigate(-1);
          break;
        case 'ArrowRight':
          this.navigate(1);
          break;
        case '+':
        case '=':
          this.zoom(this.zoomStep);
          break;
        case '-':
          this.zoom(-this.zoomStep);
          break;
        case '0':
          this.resetZoom();
          break;
      }
    });

    // Image drag functionality
    const imageWrapper = this.modal.querySelector('.product-zoom-modal__image-wrapper');
    const image = this.modal.querySelector('.product-zoom-modal__image');

    image.addEventListener('mousedown', (e) => this.startDrag(e));
    image.addEventListener('mousemove', (e) => this.drag(e));
    image.addEventListener('mouseup', () => this.endDrag());
    image.addEventListener('mouseleave', () => this.endDrag());

    // Touch events for mobile
    image.addEventListener('touchstart', (e) => this.startDrag(e.touches[0]));
    image.addEventListener('touchmove', (e) => {
      e.preventDefault();
      this.drag(e.touches[0]);
    });
    image.addEventListener('touchend', () => this.endDrag());

    // Double click to zoom
    image.addEventListener('dblclick', () => {
      if (this.zoomLevel === 1) {
        this.zoom(1);
      } else {
        this.resetZoom();
      }
    });

    // Mouse wheel zoom
    imageWrapper.addEventListener('wheel', (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -this.zoomStep : this.zoomStep;
      this.zoom(delta);
    }, { passive: false });
  }

  openZoom(mediaItem) {
    // Get all product images
    const allMediaItems = document.querySelectorAll('.product__media-item');
    this.images = [];
    this.currentIndex = 0;

    allMediaItems.forEach((item, index) => {
      const img = item.querySelector('img');
      if (img) {
        this.images.push({
          src: img.src,
          srcset: img.srcset || '',
          alt: img.alt || '',
          sizes: img.sizes || ''
        });

        if (item === mediaItem) {
          this.currentIndex = index;
        }
      }
    });

    // Show modal
    this.modal.classList.add('active');
    document.body.classList.add('product-zoom-modal-open');

    // Update UI
    this.updateImage();
    this.updateThumbnails();
    this.updateCounter();
    this.updateNavButtons();

    // Focus on close button for accessibility
    this.modal.querySelector('.product-zoom-modal__close').focus();
  }

  updateImage() {
    const image = this.modal.querySelector('.product-zoom-modal__image');
    const loading = this.modal.querySelector('.product-zoom-modal__loading');
    const currentImage = this.images[this.currentIndex];

    if (!currentImage) return;

    // Show loading
    loading.style.display = 'block';
    image.style.opacity = '0';

    // Reset zoom and position
    this.resetZoom();

    // Load image
    const img = new Image();
    img.onload = () => {
      image.src = currentImage.src;
      image.alt = currentImage.alt;

      if (currentImage.srcset) {
        image.srcset = currentImage.srcset;
      }

      if (currentImage.sizes) {
        image.sizes = currentImage.sizes;
      }

      loading.style.display = 'none';
      image.style.opacity = '1';
    };

    img.onerror = () => {
      loading.style.display = 'none';
      image.alt = 'Image failed to load';
    };

    img.src = currentImage.src;
  }

  updateThumbnails() {
    const thumbnailsContainer = this.modal.querySelector('.product-zoom-modal__thumbnails');

    if (this.images.length <= 1) {
      thumbnailsContainer.style.display = 'none';
      return;
    }

    thumbnailsContainer.style.display = 'flex';

    // Add class for many images (mobile)
    if (this.images.length > 8) {
      thumbnailsContainer.classList.add('many-images');
    }

    thumbnailsContainer.innerHTML = this.images.map((img, index) => `
      <button
        class="product-zoom-modal__thumbnail ${index === this.currentIndex ? 'active' : ''}"
        data-thumbnail-index="${index}"
        aria-label="View image ${index + 1}"
      >
        <img src="${img.src}" alt="${img.alt}" loading="lazy" />
      </button>
    `).join('');

    // Bind thumbnail clicks
    thumbnailsContainer.querySelectorAll('[data-thumbnail-index]').forEach(thumb => {
      thumb.addEventListener('click', () => {
        this.currentIndex = parseInt(thumb.dataset.thumbnailIndex);
        this.updateImage();
        this.updateThumbnails();
        this.updateCounter();
        this.updateNavButtons();
      });
    });
  }

  updateCounter() {
    const counter = this.modal.querySelector('.product-zoom-modal__counter');

    if (this.images.length <= 1) {
      counter.style.display = 'none';
      return;
    }

    counter.style.display = 'block';
    counter.querySelector('.current').textContent = this.currentIndex + 1;
    counter.querySelector('.total').textContent = this.images.length;
  }

  updateNavButtons() {
    const prevBtn = this.modal.querySelector('.product-zoom-modal__nav--prev');
    const nextBtn = this.modal.querySelector('.product-zoom-modal__nav--next');

    if (this.images.length <= 1) {
      prevBtn.style.display = 'none';
      nextBtn.style.display = 'none';
      return;
    }

    prevBtn.style.display = 'flex';
    nextBtn.style.display = 'flex';

    prevBtn.disabled = this.currentIndex === 0;
    nextBtn.disabled = this.currentIndex === this.images.length - 1;
  }

  navigate(direction) {
    const newIndex = this.currentIndex + direction;

    if (newIndex < 0 || newIndex >= this.images.length) return;

    this.currentIndex = newIndex;
    this.updateImage();
    this.updateThumbnails();
    this.updateCounter();
    this.updateNavButtons();
  }

  zoom(delta) {
    const image = this.modal.querySelector('.product-zoom-modal__image');
    const newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoomLevel + delta));

    if (newZoom === this.zoomLevel) return;

    this.zoomLevel = newZoom;
    image.style.transform = `scale(${this.zoomLevel}) translate(${this.imagePosition.x}px, ${this.imagePosition.y}px)`;

    // Update zoom button states
    const zoomIn = this.modal.querySelector('.product-zoom-modal__zoom-in');
    const zoomOut = this.modal.querySelector('.product-zoom-modal__zoom-out');

    zoomIn.disabled = this.zoomLevel >= this.maxZoom;
    zoomOut.disabled = this.zoomLevel <= this.minZoom;

    // Add/remove zoomed class for cursor change
    if (this.zoomLevel > 1) {
      image.classList.add('zoomed');
    } else {
      image.classList.remove('zoomed');
    }
  }

  resetZoom() {
    const image = this.modal.querySelector('.product-zoom-modal__image');
    this.zoomLevel = 1;
    this.imagePosition = { x: 0, y: 0 };
    image.style.transform = 'scale(1) translate(0, 0)';
    image.classList.remove('zoomed');

    // Update button states
    const zoomIn = this.modal.querySelector('.product-zoom-modal__zoom-in');
    const zoomOut = this.modal.querySelector('.product-zoom-modal__zoom-out');

    zoomIn.disabled = false;
    zoomOut.disabled = true;
  }

  startDrag(e) {
    if (this.zoomLevel <= 1) return;

    this.isDragging = true;
    this.dragStart = {
      x: e.clientX - this.imagePosition.x,
      y: e.clientY - this.imagePosition.y
    };
  }

  drag(e) {
    if (!this.isDragging) return;

    const image = this.modal.querySelector('.product-zoom-modal__image');
    this.imagePosition = {
      x: e.clientX - this.dragStart.x,
      y: e.clientY - this.dragStart.y
    };

    image.style.transform = `scale(${this.zoomLevel}) translate(${this.imagePosition.x}px, ${this.imagePosition.y}px)`;
  }

  endDrag() {
    this.isDragging = false;
  }

  close() {
    this.modal.classList.remove('active');
    document.body.classList.remove('product-zoom-modal-open');

    // Reset state
    setTimeout(() => {
      this.resetZoom();
      this.currentIndex = 0;
      this.images = [];
    }, 300);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductImageZoom();
  });
} else {
  new ProductImageZoom();
}
